import { getBrowserClient } from '@/lib/integrations/supabase/client';
import type { UserRole } from '@/lib/integrations/supabase/types';
import * as Sentry from "@sentry/nextjs";

export type { UserRole } from '@/lib/integrations/supabase/types';

export interface AuthResult {
  success: boolean;
  error?: string;
  userId?: string;
  role?: UserRole;
}

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function validatePassword(password: string): { valid: boolean; error?: string } {
  if (password.length < 8) {
    return { valid: false, error: 'Password must be at least 8 characters' };
  }
  return { valid: true };
}

const OWNER_MANAGER_ROUTES = ['/automations', '/expenses'];
const OWNER_ONLY_ROUTES: string[] = [];
const ADMIN_ONLY_ROUTES = ['/admin'];
const BARBER_ALLOWED_ROUTES = ['/home', '/schedule', '/inbox', '/sales', '/settings'];

export function canAccessRoute(pathname: string, role: UserRole | null): boolean {
  if (!role) return false;

  if (role === 'admin') return true;

  if (ADMIN_ONLY_ROUTES.some(route => pathname.startsWith(route))) {
    return false;
  }

  if (role === 'owner') return true;

  if (role === 'manager' || role === 'front_desk') {
    if (pathname.startsWith('/settings')) return true;
  }
  if (role === 'manager') {
    return !OWNER_ONLY_ROUTES.some(route => pathname.startsWith(route));
  }
  if (role === 'front_desk') {
    const restrictedRoutes = [...OWNER_MANAGER_ROUTES, ...OWNER_ONLY_ROUTES];
    return !restrictedRoutes.some(route => pathname.startsWith(route));
  }
  if (role === 'barber') {
    return BARBER_ALLOWED_ROUTES.some(route => pathname.startsWith(route));
  }
  return false;
}

export function canAccessSettingsSection(section: string, role: UserRole | null): boolean {
  if (!role) return false;
  if (role === 'admin') return true;
  if (role === 'owner') return true;
  if (role === 'manager') {
    const restrictedSections = ['app', 'billing', 'security'];
    return !restrictedSections.includes(section.toLowerCase());
  }
  if (role === 'front_desk') {
    const allowedSections = ['profile', 'appearance'];
    return allowedSections.includes(section.toLowerCase());
  }
  if (role === 'barber') {
    const allowedSections = ['profile', 'app'];
    return allowedSections.includes(section.toLowerCase());
  }
  const allowedSections = ['profile'];
  return allowedSections.includes(section.toLowerCase());
}

export function getVisibleNavItems(role: UserRole | null): string[] {
  if (!role) return [];
  if (role === 'admin') {
    return [
      '/home', '/schedule', '/inbox', '/settings',
      '/sales', '/customers', '/inventory', '/automations',
      '/admin/monitoring',
      '/testing'
    ];
  }
  if (role === 'owner') {
    return ['/home', '/schedule', '/inbox', '/settings', '/sales', '/customers', '/inventory', '/automations'];
  }
  const baseItems = ['/home', '/schedule', '/inbox', '/settings'];
  if (role === 'manager') {
    return [...baseItems, '/sales', '/customers', '/inventory', '/automations'];
  }
  if (role === 'front_desk') {
    return [...baseItems, '/customers', '/inventory'];
  }
  return ['/home', '/schedule', '/inbox', '/settings', '/sales'];
}

export function isAdmin(role: UserRole | null): boolean {
  return role === 'admin';
}

export function isOwner(role: UserRole | null): boolean {
  return role === 'owner';
}

export function isOwnerOrManager(role: UserRole | null): boolean {
  return role === 'owner' || role === 'manager';
}

export function hasElevatedAccess(role: UserRole | null): boolean {
  return role === 'admin' || role === 'owner';
}

export async function signUp(
  email: string,
  password: string,
  fullName: string,
  ownerPin?: string
): Promise<AuthResult> {
  try {
    const supabase = getBrowserClient();
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });

    if (error) {
      return { success: false, error: error.message };
    }

    if (!data.user) {
      return { success: false, error: 'Failed to create user' };
    }

    const userId = data.user.id;
    let role: UserRole = 'front_desk';

    if (ownerPin) {
      const pinResponse = await fetch('/api/auth/validate-pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, pin: ownerPin }),
      });

      const pinResult = await pinResponse.json();

      if (pinResult.valid) {
        role = 'owner';
      }
    }

    return { success: true, userId, role };
  } catch (err) {
    Sentry.captureException(err);
    return { success: false, error: 'An unexpected error occurred' };
  }
}

export async function signIn(
  email: string,
  password: string
): Promise<AuthResult> {
  try {
    const supabase = getBrowserClient();
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      Sentry.captureException(error);
      return { success: false, error: error.message };
    }

    if (!data.user) {
      return { success: false, error: 'Failed to sign in' };
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', data.user.id)
      .single() as { data: { role: string } | null };

    return {
      success: true,
      userId: data.user.id,
      role: profile?.role as UserRole,
    };
  } catch (err) {
    Sentry.captureException(err);
    return { success: false, error: 'An unexpected error occurred' };
  }
}

export async function signOut(): Promise<void> {
  const supabase = getBrowserClient();
  await supabase.auth.signOut();
}

export async function getSession() {
  const supabase = getBrowserClient();
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

export async function getUser() {
  const supabase = getBrowserClient();
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

export function getCurrentUser() {
  return null;
}

export function logoutUser() {
  signOut();
}

export function loginUser() {
  return { success: false, error: 'Use signIn() instead' };
}

export function registerUser() {
  return { success: false, error: 'Use signUp() instead' };
}
